import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Modal from '../components/Modal';

interface ModalContextData {
  isOpen: boolean;
  modalContent?: React.ReactNode;
  handleModal(
    content?: React.ReactNode,
    options?: {
      schema?: unknown;
      pathname?: string;
      state?: Record<string, unknown>;
    },
  ): void;
  closeModal: () => void;
  confirmation?: unknown;
}

const ModalContext = createContext<ModalContextData>({} as ModalContextData);

const ModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>('');
  const [confirmation, setConfirmation] = useState<unknown>();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const location = useLocation();

  // Ref to store the pathname the modal should return to on close
  const returnPathRef = useRef<string | null>(null);

  // Keep refs in sync for stable callbacks
  const locationRef = useRef(location);
  const searchParamsRef = useRef(searchParams);
  locationRef.current = location;
  searchParamsRef.current = searchParams;

  // Handle page load — clean up stale modal state from previous session
  useEffect(() => {
    const storedModalState = sessionStorage.getItem('modalState');

    if (storedModalState) {
      sessionStorage.removeItem('modalState');

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('modal');

      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { replace: true },
      );
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect page unload to store modal state
  useEffect(() => {
    const handleBeforeUnload = (): void => {
      if (isOpen) {
        sessionStorage.setItem('modalState', 'true');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen]);

  const handleModal = useCallback(
    (
      content: React.ReactNode,
      options?: {
        schema?: unknown;
        pathname?: string;
        state?: Record<string, unknown>;
      },
    ) => {
      const loc = locationRef.current;
      const params = searchParamsRef.current;

      // The pathname the modal will be displayed at
      const targetPath = options?.pathname || loc.pathname;

      // Store the return path so closeModal knows where to go
      returnPathRef.current = targetPath;

      setIsOpen(true);

      if (options?.schema) {
        setConfirmation(options.schema);
      }

      if (content) {
        setModalContent(content);
      }

      const newSearchParams = new URLSearchParams(params);
      newSearchParams.set('modal', 'true');

      navigate(
        {
          pathname: targetPath,
          search: newSearchParams.toString(),
        },
        {
          state: {
            ...loc.state,
            backgroundLocation: loc,
            ...options?.state,
          },
        },
      );
    },
    [navigate],
  );

  const closeModal = useCallback(() => {
    const params = searchParamsRef.current;
    const closePath = returnPathRef.current || locationRef.current.pathname;

    setIsOpen(false);
    setModalContent('');
    setConfirmation(undefined);
    returnPathRef.current = null;

    const newSearchParams = new URLSearchParams(params);
    newSearchParams.delete('modal');

    navigate(
      {
        pathname: closePath,
        search: newSearchParams.toString(),
      },
      {
        replace: true,
      },
    );
  }, [navigate]);

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        handleModal,
        closeModal,
        modalContent,
        confirmation,
      }}
    >
      {children}
      {isOpen && <Modal onClose={closeModal}>{modalContent}</Modal>}
    </ModalContext.Provider>
  );
};

function useModal(): ModalContextData {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }

  return context;
}

export { ModalProvider, useModal };
